import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import {
  Account,
  Job,
  Product,
} from "./types";
import { quotes, quotesData } from "../../types/api-types/crm/quotes/quotes.types";
import { quoteLineItems as QuoteLineItemType, quoteLineItemsData } from "../../types/api-types/crm/quote-line-items/quote-line-items.types";
import { ViewStoreErrorHandler } from "@intrasync/stores/utils"

interface CreateQuoteData {
  accountId: number;
  jobId: number;
  salesPersonId: number;
  quoteDate: string;
  expirationDate: string;
  totalAmount: number;
  currency: string;
  status: 'Draft' | 'Submitted' | 'UnderReview' | 'Approved' | 'Rejected' | 'Accepted' | 'ConvertedToSO' | 'Expired' | 'Cancelled';
  lineItems: Array<{
    lineNumber: number;
    panelId?: number;
    productCode?: string;
    quantity: number;
    unitPrice: number;
    discountPercent: number;
    totalPrice: number;
    description: string;
    itemType: 'Product' | 'Service' | 'Labor' | 'Material' | 'Shipping' | 'Tax' | 'Discount' | 'Other';
  }>;
}

export default class QuoteGenerationViewStore {
  rootStore: RootStore;
  errorHandler: ViewStoreErrorHandler;

  // Observables - Step Management
  currentStep = 1;
  isLoading = false;
  error: string | null = null;

  // Observables - Entity IDs
  selectedAccountId: number | undefined = undefined;
  selectedProjectId: number | undefined = undefined;
  createdQuoteId: number | undefined = undefined;

  // Observables - Created Quote
  createdQuote: quotes | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, errorHandler: false });
    this.rootStore = rootStore;

    // Initialize standardized error handling
    this.errorHandler = new ViewStoreErrorHandler(
      rootStore,
      "QuoteGenerationViewStore",
      {
        enableErrorNotifications: false,
        enableReactionBatching: true,
        debugMode: false,
      }
    );
  }

  // Computed Values - Access entities from ServiceStores
  get accounts(): Account[] {
    const store = this.rootStore.accountsStore as ServiceStore;
    return store?.entities?.map((entity: unknown) => entity as Account) || [];
  }

  get projects(): Array<{
    id: number;
    projectName: string;
    accountId?: number;
  }> {
    const store = this.rootStore.jobStore as ServiceStore;
    return (
      store?.entities?.map((entity: unknown) => {
        const job = entity as Job;
        return {
          id: job.id,
          projectName: job.name || "",
          accountId: job.accountId,
        };
      }) || []
    );
  }

  get products(): Product[] {
    const store = this.rootStore.productsStore as ServiceStore;
    return store?.entities?.map((entity: unknown) => entity as Product) || [];
  }

  get quoteLineItems(): QuoteLineItemType[] {
    if (!this.createdQuoteId) return [];
    const store = this.rootStore.quoteLineItemsStore as ServiceStore;
    return (
      (store?.entities
        ?.filter(
          (item: unknown) => {
            const lineItem = item as QuoteLineItemType;
            return lineItem.quoteId === this.createdQuoteId;
          }
        ) as unknown as QuoteLineItemType[]) || []
    );
  }

  get workflowProgress(): number {
    return ((this.currentStep - 1) / 2) * 100;
  }

  // Actions - UI State Management
  setCurrentStep = (step: number) => {
    this.currentStep = step;
  };

  setSelectedAccount = (accountId: number | undefined) => {
    this.selectedAccountId = accountId;
  };

  setSelectedProject = (projectId: number | undefined) => {
    this.selectedProjectId = projectId;
  };

  setError = (_operation: string, error: string) => {
    this.error = error;
  };

  clearError = () => {
    this.error = null;
  };

  reset = () => {
    this.currentStep = 1;
    this.selectedAccountId = undefined;
    this.selectedProjectId = undefined;
    this.createdQuoteId = undefined;
    this.createdQuote = null;
    this.error = null;
    this.isLoading = false;
  };

  // Async Actions - Quote Operations
  createQuote = async (data: CreateQuoteData) => {
    // Prevent duplicate creation if already in progress
    if (this.isLoading) {
      console.log("Quote creation already in progress, skipping duplicate call");
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Generate quote number with better format
      const timestamp = Date.now();
      const quoteNumber = `QT-${new Date().getFullYear()}-${String(timestamp).slice(-6)}`;

      console.log(`Creating quote: ${quoteNumber} with ${data.lineItems.length} line items`);

      // Calculate subtotal from line items
      const subtotal = data.lineItems.reduce((sum, item) => sum + item.totalPrice, 0);

      // Create quote with required fields matching the API types
      const newQuote: quotesData = {
        quoteNumber,
        accountId: data.accountId,
        jobId: data.jobId,
        salesPersonId: data.salesPersonId,
        quoteDate: data.quoteDate,
        expirationDate: data.expirationDate,
        subtotalAmount: subtotal,
        totalAmount: data.totalAmount,
        currency: data.currency,
        status: data.status,
        revision: 1,
      };

      // Create quote using ServiceStore
      const quoteStore = this.rootStore.quotesStore as ServiceStore;

      // Use the actual create method from ServiceStore
      const createdResult = await quoteStore.create(newQuote as unknown as Parameters<typeof quoteStore.create>[0]);

      if (!createdResult) {
        throw new Error("Failed to create quote");
      }

      const createdQuote = (Array.isArray(createdResult) ? createdResult[0] : createdResult) as unknown as quotes;

      console.log(`Quote created successfully with ID: ${createdQuote.id}`);

      // Create line items
      const lineItemStore = this.rootStore.quoteLineItemsStore as ServiceStore;
      let createdLineItemsCount = 0;

      for (const item of data.lineItems) {
        const lineItem: quoteLineItemsData = {
          quoteId: Number(createdQuote.id),
          lineNumber: item.lineNumber,
          itemType: item.itemType,
          panelId: item.panelId,
          productCode: item.productCode,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discountPercent: item.discountPercent,
          totalPrice: item.totalPrice,
          description: item.description,
          extendedPrice: item.quantity * item.unitPrice,
        };

        // Create the line item using the service store
        try {
          await lineItemStore.create(lineItem as unknown as Parameters<typeof lineItemStore.create>[0]);
          createdLineItemsCount++;
        } catch (lineError) {
          console.error("Failed to create line item:", lineError);
        }
      }

      console.log(`Created ${createdLineItemsCount} line items for quote ${createdQuote.id}`);

      runInAction(() => {
        this.createdQuoteId = typeof createdQuote.id === 'string' ? parseInt(createdQuote.id, 10) : createdQuote.id;
        // Cast the ServiceStore response to our quotes type
        this.createdQuote = {
          ...createdQuote,
          id: typeof createdQuote.id === 'string' ? parseInt(createdQuote.id, 10) : createdQuote.id
        } as quotes;
        this.currentStep = 3; // Move to summary
      });

      this.rootStore.notificationStore?.addNotification({
        message: `Quote ${createdQuote.quoteNumber} created successfully`,
        options: { variant: "success" },
      });

    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to create quote";
      });

      this.rootStore.notificationStore?.addNotification({
        message: this.error,
        options: { variant: "error" },
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Disposal method for proper cleanup
  dispose = () => {
    if (this.errorHandler) {
      this.errorHandler.dispose();
    }
  };
}